import React, { useEffect, useState } from "react";
import { FaCaretDown, FaCaretRight } from "react-icons/fa";
import { Button, Modal } from "react-bootstrap";
import { ToggleSwitch } from "./toggleSwitch";
import { toast } from "sonner";
import {
  addBulkUserPermissions,
  addSingleUserPermission,
  fetchRolesList,
  fetchUserPermissionsByModules,
  getUserRole,
  removeBulkUserPermissions,
  removeUserPermission,
  setUserRole,
} from "../../services/axios/apiManager";
import DashboardLayout from "../DashboardLayout";
import { handleErrors } from "../../services/dataManager";
import { useAppContext } from "../../services/context";

const AddEditUserPermissions = ({ navigate }) => {
  const { state } = useAppContext();
  const { permissions } = state;
  const [roles, setRoles] = useState([]);
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [modalOptions, setModalOptions] = useState({});
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [modulesPermissions, setModulesPermissions] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [granted, setGranted] = useState([]);

  const extractPermissions = () => {
    const module_perms = permissions.filter(
      (mp) => mp.module === "User_Permissions"
    );
    if (module_perms) {
      setGranted(module_perms);
    }
  };

  const getPermsData = async (uid) => {
    const res = await fetchUserPermissionsByModules(uid);
    if (res?.data?.success && res?.data?.data) {
      setModulesPermissions(res.data.data);
    } else {
      handleErrors(res, navigate);
    }
  };

  const fetchUserRole = async (uid) => {
    const response = await getUserRole(uid);
    if (response?.data?.data) {
      setUserData(response.data.data);
    }
  };

  const getRolesList = async () => {
    const response = await fetchRolesList();
    if (response?.data?.data) {
      setRoles(response.data.data);
    }
  };

  useEffect(() => {
    extractPermissions();
    const uId = localStorage.getItem("userId");
    setUserId(uId);
    if (uId) {
      getRolesList();
      fetchUserRole(uId);
      getPermsData(uId);
    } else {
      toast("Can't find a user.", { duration: 2000 });
      navigate("/userPermissions");
    }
    return () => {
      setUserId(null);
      localStorage.removeItem("userId");
    };
  }, []);

  //////////////////////////Logic for page components

  // // // Options modal for role change
  const handleRoleChange = async (role) => {
    if (!granted?.some((perm) => perm?.type === "update-role")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return;
    }
    if (userData?.role_id === role.id) return;
    setModalOptions({
      header: "Change Employee Role",
      body: `Are you sure you want to change the role of "${userData?.first_name || ""} ${userData?.last_name || ""}" to   ${role?.name && role.name}?`,
      okay: "Change",
      method: changeRole,
      data: { id: userId, role_id: role.id },
    });
    setShowOptionsModal(true);
  };

  // // // Change role on server
  const changeRole = async (data) => {
    const response = await setUserRole(data);
    if (response?.data?.success === true) {
      await fetchUserRole(userId);
    }
    setModalOptions({});
    setShowOptionsModal(false);
  };

  // // // Close options modal for role change
  const handleCloseOptionsModal = () => {
    setModalOptions({});
    setShowOptionsModal(false);
  };

  // // // Expand and collapse category Row
  const handleCollapseExpandCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Handle main category toggle
  const handleToggleCategory = async (moduleId, module_permissions = []) => {
    const isAdded = await changeOnLocal(moduleId, module_permissions);
    const ids = module_permissions?.map((perm) => perm.id);
    const data = {
      employee_id: userId,
      permissionIds: ids,
    };
    try {
      if (isAdded === true) {
        await handleRemoveBulkPermissions(data);
      } else if (isAdded === false) {
        await handleAddBulkPermissions(data);
      }
      await getPermsData(userId);
    } catch (error) {
      console.error("Error during bulk permission update:", error);
      // Revert local state if API call fails
      await getPermsData(userId);
    }
  };

  // Change category toggle button locally
  const changeOnLocal = (moduleId, module_permissions) => {
    const isOn = module_permissions?.every((perm) => perm.permissionToUser);
    if (isOn && !granted?.some((perm) => perm.type === "remove-bulk")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return null;
    }
    if (!isOn && !granted?.some((perm) => perm.type === "add-bulk")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return null;
    }
    const updatedPermissions = modulesPermissions.map((module) =>
      module.id === moduleId
        ? {
            ...module,
            module_permissions: module_permissions.map((perm) => ({
              ...perm,
              permissionToUser: !isOn,
            })),
          }
        : module
    );

    setModulesPermissions(updatedPermissions);
    return isOn;
  };

  // Adding bulk permissions
  const handleAddBulkPermissions = async (data = {}) => {
    const res = await addBulkUserPermissions(data);
    if (res?.data?.success) {
      toast.success("Module Permissions have been granted.", {
        duration: 2000,
      });
    } else {
      // toast.error("Error upon granting module permissions.", { duration: 2000 });
    }
  };

  // Removing bulk permissions
  const handleRemoveBulkPermissions = async (data = {}) => {
    const res = await removeBulkUserPermissions(data);
    if (res?.data?.success) {
      toast.success("Module Permissions have been revoked.", {
        duration: 2000,
      });
    } else {
      // toast.error("Error upon removing module permissions.", { duration: 2000 });
    }
  };

  // // // Toggling single permission
  const handleTogglePermission = async (mid, pid, currentState) => {
    if (
      currentState &&
      !granted?.some((perm) => perm.type === "remove-single")
    ) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return;
    }
    if (!currentState && !granted?.some((perm) => perm.type === "add-single")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return;
    }
    await changePermissionLocally(mid, pid);
    const data = {
      employee_id: userId || "",
      permission_id: pid || "",
    };
    try {
      if (currentState) {
        await handelRemoveSinglePermission(data);
      } else {
        await handelAddSinglePermission(data);
      }
      await getPermsData(userId);
    } catch (error) {
      await getPermsData(userId);
    }
  };

  // // // Change single permission locally
  const changePermissionLocally = async (mid, pid) => {
    const updatedPermissions = modulesPermissions.map((module) =>
      module.id === mid
        ? {
            ...module,
            module_permissions: module.module_permissions.map((perm) =>
              perm.id === pid
                ? { ...perm, permissionToUser: !perm.permissionToUser }
                : perm
            ),
          }
        : module
    );
    setModulesPermissions(updatedPermissions);
  };

  // // // Add single permission on server
  const handelAddSinglePermission = async (data = {}) => {
    const res = await addSingleUserPermission(data);
    if (res?.data?.success) {
      toast.success("Permission granted!", { duration: 2000 });
    } else {
    }
  };

  // // // Remove single permission from server
  const handelRemoveSinglePermission = async (data = {}) => {
    const res = await removeUserPermission(data);
    if (res?.data?.success) {
      toast.success("Permission revoked!", { duration: 2000 });
    } else {
    }
  };

  return (
    <DashboardLayout>
      <div
        className="pl-[35px] pr-[35px] text-light container"
        style={{
          paddingLeft: "35px",
          paddingRight: "35px",
          paddingTop: "56px",
        }}
      >
        <div className="row">
          <div className="col-12 mb-3">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a>System Settings</a>
                  </li>
                  <li className="breadcrumb-item active text-light">
                    Role Base Permission
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div
          className="container py-4 bg-dark rounded"
          style={{ color: "white", minHeight: "max-content" }}
        >
          <div className="mb-4">
            <h4>Roles</h4>
            <div className="d-flex flex-wrap gap-3">
              {roles.map((role) => (
                <div
                  key={role.id}
                  className={`card p-3 ${role?.id === userData?.role_id ? "text-light" : "bg-dark text-light"}`}
                  style={{
                    width: "200px",
                    cursor: "pointer",
                    backgroundColor:
                      role?.id === userData?.role_id ? "#008959" : "text-light",
                  }}
                  onClick={() => handleRoleChange(role)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{role.short}</h6>
                    <input
                      type="radio"
                      checked={role?.id === userData?.role_id}
                    />
                  </div>
                  <p className="mb-0 lg ">{role.name}</p>
                </div>
              ))}
            </div>
          </div>

          <h4 className="mb-3">
            Permissions for{" "}
            {`${userData?.first_name || ""} ${userData?.last_name || ""}`}
          </h4>
          <div className="row">
            <div className="col-lg-12">
              <div className="table-responsive">
                <table className="table table-hover table-striped align-middle table-borderless table-nowrap">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: "60%" }}>Category</th>
                      <th style={{ width: "10%" }}>status</th>
                      <th style={{ width: "20%" }}>permissions</th>
                      <th style={{ width: "10%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="table-dark">
                    {modulesPermissions?.map(
                      ({ module_permissions, ...module }) => (
                        <React.Fragment key={module.id}>
                          <tr className="table-dark">
                            <td>
                              <div
                                className="row"
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleCollapseExpandCategory(module.name)
                                }
                              >
                                <div className="d-flex align-items-center">
                                  {expandedCategories[module.name] ? (
                                    <FaCaretDown className="lg" />
                                  ) : (
                                    <FaCaretRight />
                                  )}
                                  {module.name}
                                </div>
                              </div>
                            </td>
                            <td
                              className={`${module.status ? "text-success" : "text-danger"}`}
                            >
                              {module.status === 1 ? "Active" : "Inactive"}
                            </td>
                            <td>
                              {module_permissions?.reduce(
                                (acc, val) =>
                                  acc + (val.permissionToUser ? 1 : 0),
                                0
                              ) || 0}
                            </td>
                            <td>
                              <div className="d-flex justify-content-between align-items-center">
                                <ToggleSwitch
                                  isOn={module_permissions?.every(
                                    (perm) => perm.permissionToUser
                                  )}
                                  onClick={() =>
                                    handleToggleCategory(
                                      module.id,
                                      module_permissions
                                    )
                                  }
                                  color={"#4caf50"}
                                />
                              </div>
                            </td>
                          </tr>
                          {expandedCategories[module.name] &&
                            module_permissions?.map((perm) => (
                              <tr key={perm.id}>
                                <td style={{ paddingLeft: "30px" }}>
                                  {perm?.description || ""}
                                </td>
                                <td>{""}</td>
                                <td>{""}</td>
                                <td>
                                  <ToggleSwitch
                                    isOn={perm?.permissionToUser || false}
                                    onClick={() => {
                                      handleTogglePermission(
                                        module.id,
                                        perm.id,
                                        perm.permissionToUser || false
                                      );
                                    }}
                                    color={"#06cfe0"}
                                  />
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <Modal
          show={showOptionsModal}
          onHide={handleCloseOptionsModal}
          centered
          className="text-light"
        >
          <Modal.Header closeButton className="bg-dark text-light border-0">
            <Modal.Title>{modalOptions.header}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark text-light">
            {modalOptions.body}
          </Modal.Body>
          <Modal.Footer className="bg-dark text-light border-0">
            <Button variant="secondary" onClick={handleCloseOptionsModal}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                modalOptions.method(modalOptions.data);
              }}
            >
              {modalOptions.okay}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default AddEditUserPermissions;
